/**
 * API для работы с реестром информационных систем
 * Интеграция с backend meta-registry-service через meta-registry API
 */

import { apiRequest } from '../config/api';
import {
  listEntries, getEntryById, getEntryByCode,
  createEntry, updateEntry, deleteEntry,
  getEntryDiagrams,
} from './metaRegistriesApi';

/** Built-in slug for the IT systems registry. */
export const SYSTEMS_REGISTRY_SLUG = 'information_systems';

// Active project ID — set by ProjectContext so non-hook API functions can use it.
let _activeProjectId = null;
export function setActiveProjectId(id) { _activeProjectId = id; }
export function getActiveProjectId() { return _activeProjectId; }

// MOCK DATA (не используется в backend-режиме, оставлено для совместимости)
const MOCK_SYSTEMS = [
  {
    id: 'sys-001',
    code: 'BILLING_SYSTEM',
    name: 'Billing System',
    type: 'system', // новое поле: 'system' или 'module'
    owner: 'Finance Team',
    domain: 'Information Systems Architecture',
    description: 'Комплексная система расчета и списания платежей клиентов.',
    updatedAt: '2025-10-10T12:00:00Z',
    tags: ['payments', 'finance', 'system'],
    modules: [ // дочерние модули/сервисы
      {
        id: 'mod-001',
        code: 'BILLING_GATEWAY',
        name: 'Billing API Gateway',
        model: 'service',
        owner: 'Finance Team',
        description: 'API Gateway для Billing System',
        tags: ['api', 'gateway']
      },
      {
        id: 'mod-002',
        code: 'BILLING_SVC',
        name: 'Billing Service',
        model: 'service',
        owner: 'Finance Team',
        description: 'Core сервис обработки платежей',
        tags: ['core', 'service']
      },
      {
        id: 'mod-003',
        code: 'BILLING_USER_SVC',
        name: 'Billing User Service',
        model: 'service',
        owner: 'Finance Team',
        description: 'Управление пользователями в Billing',
        tags: ['users', 'service']
      },
      {
        id: 'mod-004',
        code: 'BILLING_DB',
        name: 'Billing Database',
        model: 'db',
        owner: 'Finance Team',
        description: 'База данных платежей',
        tags: ['database', 'postgres']
      }
    ]
  },
  {
    id: 'sys-002',
    code: 'IDENTITY_SYSTEM',
    name: 'Identity & Access Management System',
    type: 'system',
    owner: 'Identity Team',
    domain: 'Information Systems Architecture',
    description: 'Система управления идентификацией и доступом.',
    updatedAt: '2025-10-09T09:30:00Z',
    tags: ['auth', 'identity', 'system'],
    modules: [
      {
        id: 'mod-005',
        code: 'USER_SVC',
        name: 'User Service',
        model: 'service',
        owner: 'Identity Team',
        description: 'Управление учетными записями пользователей',
        tags: ['users', 'service']
      },
      {
        id: 'mod-006',
        code: 'AUTH_SVC',
        name: 'Authentication Service',
        model: 'service',
        owner: 'Identity Team',
        description: 'Аутентификация и авторизация',
        tags: ['auth', 'service']
      },
      {
        id: 'mod-007',
        code: 'USERS_DB',
        name: 'Users Database',
        model: 'db',
        owner: 'Identity Team',
        description: 'База данных пользователей',
        tags: ['database', 'postgres']
      }
    ]
  },
  {
    id: 'sys-003',
    code: 'GATEWAY',
    name: 'API Gateway',
    type: 'module', // standalone модуль без родительской системы
    model: 'service',
    owner: 'Platform Team',
    domain: 'Technology Architecture',
    description: 'Единая точка входа для внешних клиентов, маршрутизация и безопасность.',
    updatedAt: '2025-10-10T12:00:00Z',
    tags: ['edge', 'security', 'routing']
  },
  {
    id: 'sys-004',
    code: 'CATALOG_SYSTEM',
    name: 'Product Catalog System',
    type: 'system',
    owner: 'Commerce Team',
    domain: 'Business Architecture',
    description: 'Система управления каталогом товаров и услуг.',
    updatedAt: '2025-10-11T11:20:00Z',
    tags: ['product', 'commerce', 'system'],
    modules: [
      {
        id: 'mod-008',
        code: 'CATALOG_API',
        name: 'Catalog API',
        model: 'service',
        owner: 'Commerce Team',
        description: 'API доступа к каталогу',
        tags: ['api', 'service']
      },
      {
        id: 'mod-009',
        code: 'CATALOG_DB',
        name: 'Catalog Database',
        model: 'db',
        owner: 'Commerce Team',
        description: 'База данных каталога',
        tags: ['database', 'mongo']
      }
    ]
  }
];

// Простая карта связей "система -> список id диаграмм"
// В моках из umlApi минимум есть диаграмма с id "example"
const SYSTEM_TO_DIAGRAMS = new Map([
  ['sys-001', ['example']],
  ['sys-002', ['example']],
  ['sys-003', ['example']],
]);

// Имитация сетевой задержки
const delay = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * Map MetaRegistryEntry (new backend) → legacy system shape used by UI components.
 * Preserves backward compat so components don't need to change.
 */
function normalizeEntry(entry) {
  if (!entry || !entry.id) return null;
  return {
    id: entry.id,
    code: entry.code,
    name: entry.name,
    description: entry.description || '',
    // Type from attributes.system_type; fall back to parent_id heuristic
    type: entry.attributes?.system_type
      || (entry.parent_id ? 'module' : 'system'),
    owner: entry.team?.name || entry.attributes?.owner || '',
    domain: entry.attributes?.domain || '',
    tags: Array.isArray(entry.tags)
      ? entry.tags.map(t => (typeof t === 'string' ? t : t.name))
      : [],
    updatedAt: entry.updated_at || '',
    createdAt: entry.created_at || '',
    modules: Array.isArray(entry.children) ? entry.children.map(normalizeEntry) : [],
    related_diagrams: Array.isArray(entry.related_diagrams)
      ? entry.related_diagrams.map(d => ({ id: d.doc_id, userId: d.user_id, meta: d.meta }))
      : [],
    parent_id: entry.parent_id || null,
    is_active: entry.is_active,
    status: entry.status,
    team_id: entry.team_id,
    registry_type_id: entry.registry_type_id,
    attributes: entry.attributes || {},
    _raw: entry,
  };
}

/**
 * Получить плоский список всех сущностей (систем и модулей) для автокомплита и валидации
 * @returns {Array} массив всех систем и модулей с их кодами
 */
function getFlatEntities() {
  const result = [];

  MOCK_SYSTEMS.forEach(sys => {
    if (sys.type === 'system') {
      // Добавляем саму систему
      result.push({
        id: sys.id,
        code: sys.code,
        name: sys.name,
        type: 'system',
        model: null, // система не имеет model
        owner: sys.owner,
        domain: sys.domain,
        description: sys.description,
        tags: sys.tags,
        systemId: sys.id,
        systemCode: sys.code,
        systemName: sys.name
      });

      // Добавляем все модули системы
      if (sys.modules && sys.modules.length) {
        sys.modules.forEach(mod => {
          result.push({
            ...mod,
            type: 'module',
            domain: sys.domain,
            systemId: sys.id,
            systemCode: sys.code,
            systemName: sys.name
          });
        });
      }
    } else if (sys.type === 'module') {
      // Standalone модуль
      result.push({
        id: sys.id,
        code: sys.code,
        name: sys.name,
        type: 'module',
        model: sys.model,
        owner: sys.owner,
        domain: sys.domain,
        description: sys.description,
        tags: sys.tags,
        systemId: null,
        systemCode: null,
        systemName: null
      });
    }
  });

  return result;
}

/**
 * Получить список ИС (только системы верхнего уровня)
 * @param {Object} [params]
 * @param {string} [params.projectId] - для будущей фильтрации по пространству
 * @param {string} [params.q] - строка поиска
 * @returns {Promise<Array>} список систем
 */
export async function listSystems(params = {}) {
  if (!_activeProjectId) return [];

  // Реальный запрос к backend
  try {
    const entries = await listEntries(_activeProjectId, SYSTEMS_REGISTRY_SLUG, { all: true });
    return (entries || []).map(normalizeEntry).filter(Boolean);
  } catch (error) {
    console.error('Ошибка при получении списка систем:', error);
    return [];
  }
}

/**
 * Получить информацию о системе по ID
 * @param {string} id - ID системы
 * @returns {Promise<Object|null>}
 */
export async function getSystemById(id) {
  if (!_activeProjectId) return null;
  try {
    const entry = await getEntryById(_activeProjectId, SYSTEMS_REGISTRY_SLUG, id);
    return normalizeEntry(entry);
  } catch (error) {
    console.error(`Ошибка при получении системы ${id}:`, error);
    throw error;
  }
}

/**
 * Получить информацию о системе по коду
 * @param {string} code - Код системы
 * @returns {Promise<Object|null>}
 */
export async function getSystemByCode(code) {
  if (!_activeProjectId) return null;
  try {
    const entry = await getEntryByCode(_activeProjectId, SYSTEMS_REGISTRY_SLUG, code);
    return entry ? normalizeEntry(entry) : null;
  } catch (error) {
    if (error.message?.includes('404')) {
      return null; // Система не найдена
    }
    console.error(`Ошибка при получении системы с кодом ${code}:`, error);
    throw error;
  }
}

/**
 * Создать новую систему
 * @param {Object} systemData - данные системы
 * @returns {Promise<Object>}
 */
export async function createSystem(systemData) {
  if (!_activeProjectId) throw new Error('No active project');
  try {
    const payload = {
      code: systemData.code,
      name: systemData.name,
      description: systemData.description || '',
      parent_id: systemData.parent_id || null,
      attributes: {
        system_type: systemData.type || 'system',
        domain: systemData.domain || '',
        ...(systemData.attributes || {}),
      },
      tags: (systemData.tags || []).map(t => (typeof t === 'string' ? { name: t } : t)),
    };
    const entry = await createEntry(_activeProjectId, SYSTEMS_REGISTRY_SLUG, payload);
    return normalizeEntry(entry);
  } catch (error) {
    console.error('Ошибка при создании системы:', error);
    throw error;
  }
}

/**
 * Обновить систему
 * @param {string} id - ID системы
 * @param {Object} systemData - обновленные данные
 * @returns {Promise<Object>}
 */
export async function updateSystem(id, systemData) {
  if (!_activeProjectId) throw new Error('No active project');
  try {
    const payload = {
      code: systemData.code,
      name: systemData.name,
      description: systemData.description || '',
      parent_id: systemData.parent_id || null,
      attributes: {
        system_type: systemData.type || 'system',
        domain: systemData.domain || '',
        ...(systemData.attributes || {}),
      },
      tags: (systemData.tags || []).map(t => (typeof t === 'string' ? { name: t } : t)),
    };
    const entry = await updateEntry(_activeProjectId, SYSTEMS_REGISTRY_SLUG, id, payload);
    return normalizeEntry(entry);
  } catch (error) {
    console.error(`Ошибка при обновлении системы ${id}:`, error);
    throw error;
  }
}

/**
 * Удалить систему
 * @param {string} id - ID системы
 * @returns {Promise<void>}
 */
export async function deleteSystem(id) {
  if (!_activeProjectId) throw new Error('No active project');
  try {
    await deleteEntry(_activeProjectId, SYSTEMS_REGISTRY_SLUG, id);
  } catch (error) {
    console.error(`Ошибка при удалении системы ${id}:`, error);
    throw error;
  }
}

/**
 * Удалить модуль системы
 * @param {string} moduleId - ID модуля
 * @returns {Promise<void>}
 */
export async function deleteModule(moduleId) {
  if (!_activeProjectId) throw new Error('No active project');
  try {
    await deleteEntry(_activeProjectId, SYSTEMS_REGISTRY_SLUG, moduleId);
  } catch (error) {
    console.error(`Ошибка при удалении модуля ${moduleId}:`, error);
    throw error;
  }
}

/**
 * Получить плоский список всех кодов для валидации (системы + модули)
 * @returns {Promise<Array<string>>}
 */
export async function getAllEntityCodes() {
  try {
    // listSystems now returns all entries (root + subsystems) via all=true
    const systems = await listSystems();
    return systems.map(sys => sys.code).filter(Boolean);
  } catch (error) {
    console.error('Ошибка при получении кодов сущностей:', error);
    return [];
  }
}

/**
 * Получить Map всех сущностей по коду для парсера PlantUML
 * @returns {Promise<Map<string, object>>}
 */
export async function getEntitiesMap() {
  try {
    // listSystems returns all entries flat (root + subsystems via all=true)
    const systems = await listSystems();
    const map = new Map();
    systems.forEach(sys => {
      if (!sys.code) return;
      map.set(sys.code, {
        id: sys.id,
        code: sys.code,
        name: sys.name,
        type: sys.type || (sys.parent_id ? 'module' : 'system'),
        owner: sys.owner,
        domain: sys.domain,
        description: sys.description,
        tags: sys.tags,
        systemId: sys.parent_id || null,
        systemCode: sys.parent_id ? sys.code : null,
        systemName: null,
      });
    });
    return map;
  } catch (error) {
    console.error('Ошибка при получении карты сущностей:', error);
    return new Map();
  }
}

/**
 * Получить информацию о конкретной системе или модуле по коду
 * @param {string} code
 * @returns {Promise<object|null>}
 */
export async function getEntityByCode(code) {
  try {
    return await getSystemByCode(code);
  } catch (error) {
    console.error(`Ошибка при получении сущности по коду ${code}:`, error);
    return null;
  }
}

/**
 * Получить связанные с системой диаграммы
 * @param {Object} params
 * @param {string} params.systemId
 * @param {string} [params.systemCode]
 * @returns {Promise<Array<{id: string, meta: object}>>}
 */
export async function listRelatedDiagrams(params) {
  const { systemId } = params || {};

  if (!systemId || !_activeProjectId) return [];

  try {
    const diagrams = await getEntryDiagrams(_activeProjectId, SYSTEMS_REGISTRY_SLUG, systemId);
    return diagrams.map(d => ({ id: d.doc_id, userId: d.user_id, meta: d.meta }))
      .sort((a, b) =>
        // meta.updatedAt is a frontend-defined field stored inside opaque JSON blob
        new Date(b.meta?.updatedAt || 0) - new Date(a.meta?.updatedAt || 0)
      );
  } catch (error) {
    console.error('Ошибка при получении связанных диаграмм:', error);
    return [];
  }
}

/**
 * Связать диаграмму с системой
 * @param {Object} params
 * @param {string} params.systemId - ID системы
 * @param {string} params.diagramId - ID диаграммы
 * @returns {Promise<void>}
 */
export async function linkDiagramToSystem(params) {
  const { systemId, diagramId } = params || {};

  if (!systemId || !diagramId) {
    throw new Error('systemId и diagramId обязательны');
  }

  // TODO: Backend link/unlink endpoints not yet implemented
}

/**
 * Отвязать диаграмму от системы
 * @param {Object} params
 * @param {string} params.systemId - ID системы
 * @param {string} params.diagramId - ID диаграммы
 * @returns {Promise<void>}
 */
export async function unlinkDiagramFromSystem(params) {
  const { systemId, diagramId } = params || {};

  if (!systemId || !diagramId) {
    throw new Error('systemId и diagramId обязательны');
  }

  // TODO: Backend link/unlink endpoints not yet implemented
}

/**
 * Get K8s infrastructure for a system by code.
 * Infrastructure endpoint has no meta-registry equivalent — stays on /systems alias.
 * @param {string} code - System code
 * @returns {Promise<{entry, pods, stats}|null>}
 */
export async function getSystemInfrastructure(code) {
  try {
    return await apiRequest(`/systems/code/${encodeURIComponent(code)}/infrastructure`, {
      method: 'GET',
    });
  } catch (error) {
    if (error.message?.includes('404')) return null;
    console.error(`Error fetching infrastructure for ${code}:`, error);
    return null;
  }
}

/**
 * Переключить режим использования backend
 * @param {boolean} enabled
 */
export function setBackendEnabled(_enabled) { /* no-op: always backend */ }
export function isBackendEnabled() { return true; }
